import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { DbHighlight } from '../lib/supabase'

export const HIGHLIGHT_COLORS = [
  { name: 'Yellow', value: '#fef08a' },
  { name: 'Green', value: '#bbf7d0' },
  { name: 'Blue', value: '#bfdbfe' },
  { name: 'Pink', value: '#fbcfe8' },
  { name: 'Orange', value: '#fed7aa' },
] as const

interface UseHighlightsReturn {
  highlights: DbHighlight[]
  loading: boolean
  error: string | null
  createHighlight: (
    textContent: string,
    startOffset: number,
    endOffset: number,
    color?: string
  ) => Promise<DbHighlight | null>
  updateHighlight: (id: string, updates: { color?: string; note?: string | null }) => Promise<void>
  deleteHighlight: (id: string) => Promise<void>
  exportAsMarkdown: () => string
  refresh: () => Promise<void>
}

export function useHighlights(
  articleId: string | undefined,
  userId: string | undefined
): UseHighlightsReturn {
  const [highlights, setHighlights] = useState<DbHighlight[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchHighlights = useCallback(async () => {
    if (!articleId || !userId) {
      setHighlights([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('highlights')
        .select('*')
        .eq('article_id', articleId)
        .eq('user_id', userId)
        .order('start_offset', { ascending: true })

      if (fetchError) throw fetchError

      setHighlights(data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch highlights')
    } finally {
      setLoading(false)
    }
  }, [articleId, userId])

  useEffect(() => {
    fetchHighlights()
  }, [fetchHighlights])

  const createHighlight = useCallback(
    async (
      textContent: string,
      startOffset: number,
      endOffset: number,
      color: string = HIGHLIGHT_COLORS[0].value
    ): Promise<DbHighlight | null> => {
      if (!articleId || !userId) return null

      try {
        const { data, error: insertError } = await supabase
          .from('highlights')
          .insert({
            user_id: userId,
            article_id: articleId,
            text_content: textContent,
            start_offset: startOffset,
            end_offset: endOffset,
            color,
          })
          .select()
          .single()

        if (insertError) throw insertError

        if (data) {
          setHighlights((prev) =>
            [...prev, data].sort((a, b) => a.start_offset - b.start_offset)
          )
          return data
        }
        return null
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create highlight')
        return null
      }
    },
    [articleId, userId]
  )

  const updateHighlight = useCallback(
    async (id: string, updates: { color?: string; note?: string | null }) => {
      try {
        const { error: updateError } = await supabase
          .from('highlights')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id)

        if (updateError) throw updateError

        setHighlights((prev) =>
          prev.map((h) => (h.id === id ? { ...h, ...updates } : h))
        )
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update highlight')
      }
    },
    []
  )

  const deleteHighlight = useCallback(async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('highlights')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError

      setHighlights((prev) => prev.filter((h) => h.id !== id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete highlight')
    }
  }, [])

  const exportAsMarkdown = useCallback((): string => {
    if (highlights.length === 0) return ''

    return highlights
      .map((h) => {
        let md = `> ${h.text_content}`
        if (h.note) md += `\n\n**Note:** ${h.note}`
        return md
      })
      .join('\n\n---\n\n')
  }, [highlights])

  return {
    highlights,
    loading,
    error,
    createHighlight,
    updateHighlight,
    deleteHighlight,
    exportAsMarkdown,
    refresh: fetchHighlights,
  }
}
